/**
 * Installment Credit Item Component
 * Displays special installment conditions for a specific product/service
 */
import React, { FC, memo, useMemo } from 'react'

import { TCartItem } from '@/entities/cart'
import { CreditItem, getInstalmentPayPrice, NotificationCard, SpecialCondition } from '@/entities/Payments'

import { useGetRelatedProducts } from '@/shared/lib/hooks/useGetRelatedProducts'
import { LocalProduct } from '@/shared/types/deprecated-types'

interface InstallmentCreditItemProps {
	condition: SpecialCondition
	relatedId: number
	relatedPrice: number
	onModalOpen?: (condition: SpecialCondition) => void
	toOrder?: (installmentCode: string, serviceId?: number) => void
	isLastCondition?: boolean
}

const getActiveInstallmentText = (cardTitle: string): string => `Вам доступна рассрочка ${cardTitle}`

const getActiveCondition = (
	condition: SpecialCondition,
	relatedProducts: (TCartItem | LocalProduct)[]
): TCartItem | LocalProduct | undefined =>
	relatedProducts.find(({ ID }) => condition.productList.some(({ PRODUCT_ID }) => ID === PRODUCT_ID))

export const InstallmentCreditItem: FC<InstallmentCreditItemProps> = memo(
	({ condition, relatedId, relatedPrice, onModalOpen, toOrder, isLastCondition }) => {
		const relatedProducts = useGetRelatedProducts(relatedId)

		// Find if current product/service has active condition
		const activeCondition = useMemo(() => getActiveCondition(condition, relatedProducts), [condition, relatedProducts])

		const isActive = !!activeCondition
		const isSingleProduct = condition?.productList?.length === 1 && condition?.productList[0]?.PRODUCT_ID === relatedId

		// Determine action: direct order or modal for service selection
		const handleApply = () => {
			if (isActive || isSingleProduct) {
				toOrder?.(condition.installmentCode, activeCondition?.ID)
			} else {
				onModalOpen?.(condition)
			}
		}

		// Show active status or general description
		const description = isActive ? getActiveInstallmentText(condition.installmentCode) : condition.description

		return (
			<CreditItem
				key={condition.installmentCode}
				period={`${condition.installmentValue} мес.`}
				payCount={getInstalmentPayPrice(relatedPrice, condition.installmentValue)}
				firstPay={0}
				onApply={handleApply}
				withSeparate={isLastCondition}
				footerNode={description ? <NotificationCard description={description} disabled={isActive} /> : null}
			/>
		)
	}
)
